// packages/renderer/src/search/column-filter.ts
//
// Per-column filter matching, shared by the table grid (live + faceted) and
// the read-only view windows. Pure and DOM-free so it's unit-testable.
//
// A filter is a COMMA-SEPARATED list of tokens. Each token may be negated with
// a leading `!` and/or anchored with a leading `^` (starts-with), `=` (exact
// match), or one of `>=` `<=` `>` `<` (order comparison) — these anchors are
// mutually exclusive, since a term cannot be both "starts with" and "at
// least". A row passes when it matches at least one positive token (or there
// are none) and matches no negative token:
//
//   `Sweden,Norway`      → Sweden OR Norway
//   `!Closed,!Cancelled` → everything except those two
//   `Open,!urgent`       → Open, but not the urgent ones
//   `^S`                 → cells that START WITH "S"
//   `!^S`                → cells that do NOT start with "S"
//   `=foo`               → cells that are EXACTLY "foo"
//   `!=foo`              → cells that are NOT exactly "foo"
//   `>=100`              → cells AT LEAST "100"
//   `<100`               → cells BEFORE "100"
//   `"Berlin, DE",Zurich` → a value containing a comma must be quoted
//
// Two tokens can also be joined with a standalone uppercase `AND`, which is the
// one thing a comma cannot say — a comma ORs. `OR` is accepted as a spelled-out
// comma, and `AND` binds tighter, so the shape matches the search box
// (`text-search.ts`) that users meet first:
//
//   `!NULL AND Biden`    → has a value AND contains "Biden"
//   `^B AND !Bush`       → starts with "B" but is not a Bush
//   `a AND b,c`          → (a AND b) OR c
//   `a OR b`             → same as `a,b`
//
// Only UPPERCASE `AND`/`OR` standing alone between spaces are operators, so
// "brand" and "Andrew" are ordinary text, and a term that really contains the
// word is quoted: `"Salt AND Pepper"`. A comma-separated NEGATIVE token keeps
// excluding on its own (`Open,!urgent` is "Open but not urgent") — only an
// explicit `AND` builds a group that must match as a whole.
//
// Per-token semantics (case-insensitive):
//   • plain text        → the `defaultSubstring` option decides: substring by
//     default, exact when the user has turned that setting off. Every other
//     form below says which it wants and ignores the setting. On a `number`
//     column a bare term ALWAYS means equals, and compares numerically — see
//     the `=text` bullet below, which it shares its reading with.
//   • `*text*`          → CONTAINS, said explicitly.
//   • `text*` / `^text` → starts-with, anchored at the first character.
//   • `*text`           → ends-with.
//   • `"text"`          → exact, when it is one entry of a LIST. The whole
//     input in quotes is not a list at all — see `isListExpression`.
//   • `=text`           → exact match against the WHOLE cell (not trimmed). On
//     a `number` column this compares NUMERICALLY (`compare-cell.ts`'s
//     `compareKey`), so `=180` matches `180`, `180.0` and `" 180 "` alike — a
//     `number` cell has many text spellings of the same value, and string
//     equality answers a different question than the user asked. Falls back
//     to text equality when the term itself is not a valid number.
//   • `>=text` `<=text`
//     `>text` `<text`   → ORDER comparison, type-aware per `compare-cell.ts`:
//     numeric for a `number` column, calendar/instant for `date`/`datetime`,
//     lexicographic text otherwise. False for an empty cell — a comparison
//     against nothing is not an order relation — so `!>=x` passes an empty
//     cell the same way every other negated test does.
//   • `!text`           → NOT. Because a null or empty cell never contains a
//     non-empty term, `!true` on a boolean column also surfaces the empty rows.
//   • `NULL`            → cell is null/undefined or (after trim) empty.
//   • `!NULL`           → cell has any non-empty value.
//   • `!` alone         → same as `!NULL` (cell has a value).
//   • empty query       → matches everything (no filter).
//
// A `*` BETWEEN two pieces of text is a wildcard as well, standing for any run
// of characters: `*Marc*Julian*` finds "Dr Marc Julian Smith". The segments must
// appear in the order they were typed and may not overlap, and the outer stars
// still say where the whole pattern is pinned — `Marc*Julian*` is anchored at
// the first character, `*Marc*Julian` at the last.
//
// `*` is a wildcard OUTSIDE quotes only, so `"a*b"` is the literal value, and a
// token that is nothing but stars stays literal too (`*` is a search for an
// asterisk, since an empty term already means "is this cell blank"). `=a*b` is
// literal for the same reason: `=` asks for the whole cell, verbatim, and a `*`
// after a comparison anchor is literal too — `>=a*` compares against the text
// "a*", since the star-wildcard guard does not run once `cmp` is set.
//
// One limitation worth knowing: a BARE token whose term needs quoting (it holds
// a comma, or starts with `!`/`^`/`=`/`*`) cannot survive
// `compose → parse`, because the quotes it gains come back meaning `exact`.
// Give such a token an explicit anchor — the view pills already emit `=`. The
// grid funnel does not (`filter-popover.ts`, `data-table.ts`); it composes a
// BARE term, which is why a number column's bare-term-means-equals rule
// matters at the language level and not just for the `=` anchor.
//
// `NULL` is matched as a whole token (case-insensitive), so a plain search for
// the literal text "null" inside a cell is intentionally not reachable — the
// null test wins. `^` and `=` both beat it: `^NULL` looks for cells starting
// with the TEXT "null", `=NULL` looks for cells that ARE exactly "null". Every
// comparison anchor beats it too: `>=NULL` compares against the literal text
// "null", not the blank test. Quote a token to search for a literal leading
// `!`, `^`, `=`, `>` or `<` (`"^caret"`).
//
// An `array` column (pass `{ type: 'array' }`) matches PER MEMBER: the cell is
// taken apart by `array-cell.ts` and a token that hits any one member hits
// the cell. `=Foo` therefore selects the rows whose list CONTAINS exactly `Foo`,
// which is what the funnel dropdown needs — its tokens are exact, and the whole
// cell (`Foo,Bar`) is never exactly one value. Negation still reads as "no
// member matches", and `NULL` as "no members at all".
import { arrayMembers } from './array-cell.js';
import { compareKey, satisfiesCmp } from './compare-cell.js';
/** Is a cell value considered empty/null for filtering purposes? */
function isNullish(value) {
    return value == null || String(value).trim() === '';
}
/**
 * Does this input want to be read as a LIST of include/exclude values, or as
 * one piece of plain text?
 *
 * The filter box takes both, and guessing wrong is the difference between
 * finding the rows and finding nothing. Two rules, in order:
 *
 *  1. The WHOLE input in quotes, with no other quote in it, is plain text. This
 *     is the deliberate override — the only way to search for a value that
 *     really contains a comma or a leading `!`.
 *  2. Otherwise it is a list only if it carries a mark that says so: a comma,
 *     `!`, `^`, `=`, `*`, or a standalone `AND` / `OR`. `*` and `=` are in that
 *     set because `foo*` and `=foo` mean nothing as literal text.
 *
 * Anything else is plain text, so an ordinary phrase keeps working with no
 * syntax to learn.
 */
export function isListExpression(raw) {
    const q = String(raw ?? '').trim();
    if (q === '')
        return false;
    if (isFullyQuoted(q))
        return false;
    return /[,!^=*<>]/.test(q) || /\s(AND|OR)(?=[\s,]|$)/.test(q);
}
/** The whole input in quotes, with no other quote inside it. */
function isFullyQuoted(q) {
    return q.length >= 2 && q.startsWith('"') && q.endsWith('"') && (q.match(/"/g) ?? []).length === 2;
}
/**
 * The text a plain-text input is really asking for: the inner text when the
 * user quoted the whole thing, otherwise the input as typed.
 */
export function plainTextOf(raw) {
    const q = String(raw ?? '').trim();
    return isFullyQuoted(q) ? q.slice(1, -1) : q;
}
/**
 * Split a raw filter string into its tokens. Commas separate; double quotes
 * protect a comma inside a value (`""` inside a quoted run is a literal quote).
 * A leading `!` (negate) and `^` (starts-with) OR `=` (exact) are consumed in
 * that order, outside quotes only — `^` and `=` are mutually exclusive anchors,
 * so once one is consumed the other is treated as literal text. A token whose
 * text is empty is dropped, so `a,,b` is just `a` OR `b` — but a lone `!`
 * survives, since it means "has a value".
 *
 * A standalone uppercase `AND` / `OR` between tokens is an operator: `OR` reads
 * as a comma, and `AND` sets `and` on the token that follows it.
 */
export function parseColumnFilter(raw) {
    const tokens = [];
    let buf = '';
    let sawText = false; // did this token contain any character at all?
    let quoted = false; // currently inside a quoted run
    let hadQuote = false; // this token was quoted, so keep its whitespace verbatim
    let negate = false;
    let prefix = false;
    let exact = false;
    let cmp = null;
    let atStart = true; // still eligible to consume a leading `!` / `^` / `=`
    let pendingAnd = false; // an `AND` was read; it belongs to the NEXT token
    const flush = () => {
        let term = hadQuote ? buf : buf.trim();
        // Excel's wildcard, and only outside quotes: `"a*b"` is the literal value.
        // A token that is nothing but stars keeps them as text — `*` alone is a
        // search for an asterisk, not a match-everything, because an empty term
        // already means "is this cell blank" further down.
        let star = null;
        if (!hadQuote && !exact && !prefix && !cmp && /[^*]/.test(term)) {
            const lead = term.startsWith('*');
            const tail = term.endsWith('*');
            if (lead && tail && term.length > 1) {
                star = 'contains';
                term = term.slice(1, -1);
            }
            else if (tail) {
                star = 'prefix';
                term = term.slice(0, -1);
            }
            else if (lead) {
                star = 'suffix';
                term = term.slice(1);
            }
        }
        if (sawText || negate) {
            const token = { term, negate };
            if (prefix || star === 'prefix')
                token.prefix = true;
            if (star === 'suffix')
                token.suffix = true;
            if (star === 'contains')
                token.contains = true;
            if (cmp)
                token.cmp = cmp;
            // A quoted entry in a LIST is an exact value — `"Foo Bar","Baz"` is a
            // two-value picker, not two substrings. The whole-input quoted form never
            // reaches here: `isListExpression` sends it down the plain-text path.
            if (exact || (hadQuote && !star))
                token.exact = true;
            // Nothing to join to when this is the first token — a leading `AND` is
            // dropped rather than left dangling for `composeColumnFilter` to emit.
            if (pendingAnd && tokens.length > 0)
                token.and = true;
            tokens.push(token);
        }
        pendingAnd = false;
        buf = '';
        sawText = false;
        quoted = false;
        hadQuote = false;
        negate = false;
        prefix = false;
        exact = false;
        cmp = null;
        atStart = true;
    };
    for (let i = 0; i < raw.length; i++) {
        const ch = raw[i];
        // An operator needs whitespace in front of it and a token boundary behind,
        // so `brand` and `Andrew` stay text and only ` AND ` / ` OR ` separate. The
        // test is case-SENSITIVE, matching the search box: lowercase "and" is a word.
        if (!quoted && /\s/.test(ch)) {
            const op = /^\s+(AND|OR)(?=[\s,]|$)/.exec(raw.slice(i));
            if (op && (sawText || negate)) {
                flush();
                pendingAnd = op[1] === 'AND';
                i += op[0].length - 1;
                continue;
            }
        }
        if (ch === '"') {
            // A quote anywhere in the token opens/closes a quoted run; a doubled
            // quote inside one is an escaped literal.
            if (quoted && raw[i + 1] === '"') {
                buf += '"';
                sawText = true;
                i++;
                continue;
            }
            quoted = !quoted;
            hadQuote = true;
            sawText = true;
            atStart = false;
            continue;
        }
        if (ch === ',' && !quoted) {
            flush();
            continue;
        }
        // `!`, `^` and `=` are modifiers, not text — each may appear once, before
        // the term, in the order `!` then `^`/`=`. `^` and `=` are mutually
        // exclusive anchors: whichever is seen first wins, and a second one (or
        // one after the other anchor) is treated as literal text.
        if (ch === '!' && !quoted && atStart && !negate && !prefix && !exact) {
            negate = true;
            continue;
        }
        // `>` / `<`, optionally followed by `=`. Before the `=` branch, which would
        // otherwise eat the second character of `>=` as the exact-match anchor.
        if ((ch === '>' || ch === '<') && !quoted && atStart && !cmp && !prefix && !exact) {
            if (raw[i + 1] === '=') {
                cmp = `${ch}=`;
                i++;
            }
            else {
                cmp = ch;
            }
            continue;
        }
        if (ch === '=' && !quoted && atStart && !cmp && !prefix && !exact) {
            exact = true;
            continue;
        }
        if (ch === '^' && !quoted && atStart && !cmp && !prefix && !exact) {
            prefix = true;
            continue;
        }
        if (!(atStart && !quoted && /\s/.test(ch)))
            atStart = false;
        buf += ch;
        if (!/\s/.test(ch))
            sawText = true;
    }
    flush();
    return tokens;
}
/**
 * Does a term need quoting to survive a `parseColumnFilter` round-trip? A
 * leading `!`, `^` or `=` must be quoted or it would come back as a modifier,
 * and a term carrying a standalone `AND` / `OR` would come back split in two.
 */
function needsQuoting(term) {
    return term.includes(',') || term.includes('"') || term !== term.trim() || term === '' || term.startsWith('!') || term.startsWith('^') || term.startsWith('=') || term.startsWith('>') || term.startsWith('<') || /\s(AND|OR)(?=[\s,]|$)/.test(term);
}
/** Render tokens back into a filter string, quoting terms that need it. */
export function composeColumnFilter(tokens) {
    let out = '';
    tokens.forEach((t, i) => {
        const body = t.term === '' && t.negate
            ? '' // a bare `!` — "has a value"
            : needsQuoting(t.term)
                ? `"${t.term.replace(/"/g, '""')}"`
                : t.term;
        // An EXACT token whose term had to be quoted is written as the quoted form
        // alone: quotes already mean exact inside a list, so `="!bang"` would say it
        // twice and, worse, would not survive `compose → parse → compose`.
        // `^` rather than `foo*` for a prefix, because the two mean the same and the
        // anchor is what every existing composer and test already round-trips.
        const quoted = body !== t.term;
        const anchored = t.cmp ? `${t.cmp}${body}` : t.exact ? (quoted ? body : `=${body}`) : t.prefix ? `^${body}` : t.contains ? `*${body}*` : t.suffix ? `*${body}` : body;
        const text = (t.negate ? '!' : '') + anchored;
        // `and` joins to the token before it, so it cannot open the expression: a
        // caller that dropped the first token (the view pills do) must not get a
        // string that starts with " AND ".
        if (i === 0)
            out = text;
        else
            out += t.and ? ` AND ${text}` : `,${text}`;
    });
    return out;
}
/**
 * Split the flat token list into AND-groups: a token flagged `and` continues the
 * group before it, anything else opens a new one.
 */
export function groupColumnFilter(tokens) {
    const groups = [];
    for (const t of tokens) {
        const last = groups[groups.length - 1];
        if (t.and && last)
            last.push(t);
        else
            groups.push([t]);
    }
    return groups;
}
/**
 * Is this term a PATTERN — text with a wildcard standing between two pieces of
 * it — rather than a plain string to look for?
 *
 * The `/[^*]/` half is the same rule the parser applies at the ends: a token of
 * nothing but stars is a search for an asterisk, because an empty term already
 * means "is this cell blank".
 */
export function hasInnerWildcard(term, token) {
    if (token?.literal || token?.exact)
        return false;
    return term.includes('*') && /[^*]/.test(term);
}
/**
 * Does `haystack` hold each of `parts` in the order given, with `anchorStart` /
 * `anchorEnd` pinning the first and last to the ends of the cell?
 *
 * Leftmost-greedy, and two parts may never share the same characters: `*aa*aa*`
 * wants four a's, not two. Greedy is safe here because taking each part as early
 * as possible leaves the most room for the ones after it.
 *
 * An empty part is skipped — it comes from `**`, or from a star sitting against
 * an anchored end, and the star beside it already allows anything.
 */
function matchesParts(haystack, parts, anchorStart, anchorEnd) {
    const last = parts.length - 1;
    let at = 0;
    for (let i = 0; i <= last; i++) {
        const part = parts[i];
        if (part === '')
            continue;
        if (i === 0 && anchorStart) {
            if (!haystack.startsWith(part))
                return false;
            at = part.length;
        }
        else if (i === last && anchorEnd) {
            // `< at` rather than `<=`: the tail must begin at or after everything
            // already consumed, so it cannot reuse those characters.
            if (!haystack.endsWith(part) || haystack.length - part.length < at)
                return false;
            at = haystack.length;
        }
        else {
            const found = haystack.indexOf(part, at);
            if (found < 0)
                return false;
            at = found + part.length;
        }
    }
    return true;
}
/**
 * Does one piece of text satisfy a token's anchoring? Not trimmed — `=` is an
 * exact match against the whole thing, so " foo " is not `=foo`.
 *
 * A term carrying a star BETWEEN two pieces of text is matched as a pattern.
 * The parser only ever took the stars off the ENDS — an inner one stayed in the
 * term and was then hunted for as a literal asterisk, so `*Marc*Julian*` looked
 * for the characters `marc*julian` and found nothing. The outer stars still say
 * where the pattern is pinned; the inner ones stand for any run of characters.
 */
function matchesText(value, token, defaultSubstring) {
    const haystack = String(value ?? '').toLowerCase();
    const needle = token.term.toLowerCase();
    if (token.exact)
        return haystack === needle;
    if (hasInnerWildcard(needle, token)) {
        const parts = needle.split('*');
        if (token.prefix)
            return matchesParts(haystack, parts, true, false);
        if (token.suffix)
            return matchesParts(haystack, parts, false, true);
        if (token.contains)
            return matchesParts(haystack, parts, false, false);
        // No anchor: the setting decides, exactly as for a starless term — floating
        // when a bare value means "contains", pinned at both ends when it means
        // "is".
        return matchesParts(haystack, parts, !defaultSubstring, !defaultSubstring);
    }
    if (token.prefix)
        return haystack.startsWith(needle);
    if (token.suffix)
        return haystack.endsWith(needle);
    if (token.contains)
        return haystack.includes(needle);
    // No anchor of any kind: the setting decides which way a bare value leans.
    return defaultSubstring ? haystack.includes(needle) : haystack === needle;
}
/** An array cell is empty when it has no members, whatever its spelling. */
function isEmptyCell(value, members) {
    return members ? members.length === 0 : isNullish(value);
}
/**
 * Does `value` satisfy a single filter token? `members` is non-null for an
 * `array` column, in which case the token tests each member and one hit is
 * enough.
 */
function matchesTerm(value, token, members, defaultSubstring, type, now) {
    const term = token.term;
    // A comparison is an order test against the term as written — `>=NULL` looks
    // for cells at or after the literal text "null", not for blank ones.
    if (token.cmp) {
        if (members)
            return members.some((m) => satisfiesCmp(m, term, token.cmp, type, now));
        return satisfiesCmp(value, term, token.cmp, type, now);
    }
    // An empty term (a lone `!`) always tests emptiness — an anchor cannot anchor
    // nothing. `NULL` tests emptiness too, unless an anchor asked for the literal
    // text.
    if (term.trim() === '')
        return isEmptyCell(value, members);
    if (!token.prefix && !token.exact && !token.suffix && !token.contains && term.toUpperCase() === 'NULL') {
        return isEmptyCell(value, members);
    }
    // Equality on a NUMBER column compares NUMERICALLY, through the same
    // `compareKey` reading `satisfiesCmp` uses above, so `=180`/`>=180` can never
    // disagree about what "180" is. A `number` cell has many text spellings of
    // the same value ("180", "180.0", " 180 ") — string equality was answering a
    // different question than the user asked. A BARE term (no anchor at all)
    // also means equals here, never substring, regardless of `defaultSubstring`
    // — "No operator means =" — which only applies to `=` and to a bare term,
    // not to `^`/`*` anchors, so those still read as text.
    //
    // Falls back to ordinary text matching when the term itself is not a valid
    // number: a `number` column can hold dirt after an import, and `abc` typed
    // into its funnel should still find a cell containing "abc" rather than
    // silently matching nothing.
    if (type === 'number' && !token.prefix && !token.suffix && !token.contains) {
        const termKey = compareKey(term, 'number');
        if (termKey !== null) {
            if (members)
                return members.some((m) => compareKey(m, 'number') === termKey);
            return compareKey(value, 'number') === termKey;
        }
    }
    if (members)
        return members.some((m) => matchesText(m, token, defaultSubstring));
    return matchesText(value, token, defaultSubstring);
}
/** Does the cell satisfy every token of one AND-group? */
function matchesGroup(value, group, members, defaultSubstring, type, now) {
    return group.every((t) => (t.negate ? !matchesTerm(value, t, members, defaultSubstring, type, now) : matchesTerm(value, t, members, defaultSubstring, type, now)));
}
/**
 * Does `value` satisfy the per-column filter `rawQuery`?
 *
 * `opts.type` is the column's type. Only `array` changes anything — it switches
 * matching to per-member (see the header). Every other type reads the cell as
 * one value, so a caller that knows no type can leave it out.
 */
export function matchesColumnFilter(value, rawQuery, opts) {
    const groups = groupColumnFilter(parseColumnFilter(rawQuery));
    if (groups.length === 0)
        return true;
    const members = opts?.type === 'array' ? arrayMembers(value) : null;
    const defaultSubstring = opts?.defaultSubstring ?? true;
    const type = opts?.type;
    // Resolved ONCE per call, so the two halves of `>=-3m AND <=today` cannot
    // land on different sides of midnight.
    const now = opts?.now ?? new Date();
    // A comma-separated NEGATIVE token on its own still excludes outright, which
    // is what makes `Open,!urgent` mean "Open but not urgent" rather than "Open OR
    // not-urgent". Only an explicit `AND` puts a negation inside a group, where it
    // is one condition among several instead of a veto over the whole filter.
    const vetoes = groups.filter((g) => g.length === 1 && g[0].negate);
    for (const g of vetoes) {
        if (matchesTerm(value, g[0], members, defaultSubstring, type, now))
            return false;
    }
    const required = groups.filter((g) => !(g.length === 1 && g[0].negate));
    if (required.length === 0)
        return true;
    return required.some((g) => matchesGroup(value, g, members, defaultSubstring, type, now));
}
//# sourceMappingURL=column-filter.js.map